import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { theme } from '../../lib/theme';
import Badge from '../common/Badge';
import { mockAgentCommissions, mockPMProperties } from '../../lib/mockData';
import CommissionsView from '../agent/CommissionsView';
import { supabase } from '../../lib/supabase';
import { formatDateShort as fmtDate } from '../../lib/dateFormat';

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt = (v) => {
  if (typeof v !== 'number') return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
};
const pct = (n, d) => d ? `${((n / d) * 100).toFixed(1)}%` : '0%';

// ─── Shared styles ──────────────────────────────────────────────────────────
const cardStyle = {
  backgroundColor: theme.colors.card, borderRadius: theme.radius.md,
  boxShadow: theme.shadow.sm, padding: '20px',
};
const clickableCard = {
  ...cardStyle, cursor: 'pointer', transition: 'box-shadow 0.15s ease, transform 0.15s ease',
};
const breadcrumbStyle = {
  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20,
  fontSize: 13, color: theme.colors.textLight, flexWrap: 'wrap',
};
const breadLink = {
  color: theme.colors.primary, cursor: 'pointer', fontWeight: 500,
  textDecoration: 'none',
};
const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontWeight: 600,
  color: theme.colors.text, fontSize: 13, borderBottom: `2px solid ${theme.colors.border}`,
};
const tdStyle = {
  padding: '10px 12px', fontSize: 13, color: theme.colors.text,
  borderBottom: `1px solid ${theme.colors.border}`,
};
const printBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
  background: 'white', color: theme.colors.text, fontSize: 13,
  fontWeight: 600, cursor: 'pointer',
};

// ─── Print helper ───────────────────────────────────────────────────────────
const handlePrint = (ref) => {
  const content = ref.current;
  if (!content) return;
  const printWin = window.open('', '_blank');
  printWin.document.write(`<!DOCTYPE html><html><head><title>Sabal Real Estate — Statement</title>
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1A2B3C; }
      table { width: 100%; border-collapse: collapse; margin: 16px 0; }
      th { text-align: left; padding: 8px 12px; font-weight: 600; font-size: 13px; border-bottom: 2px solid #E2E8F0; }
      td { padding: 8px 12px; font-size: 13px; border-bottom: 1px solid #E2E8F0; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 3px solid #1B5E54; }
      .logo { font-size: 28px; font-weight: 800; color: #1B5E54; }
      .subtitle { font-size: 12px; color: #64748B; }
      .totals { margin-top: 20px; padding: 16px; background: #F5F7FA; border-radius: 8px; }
      .total-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 14px; }
      .total-row.bold { font-weight: 700; font-size: 16px; border-top: 2px solid #E2E8F0; padding-top: 8px; margin-top: 8px; }
      @media print { body { padding: 20px; } .no-print { display: none; } }
    </style>
  </head><body>`);
  printWin.document.write(content.innerHTML);
  printWin.document.write('</body></html>');
  printWin.document.close();
  setTimeout(() => { printWin.print(); }, 300);
};

// ═══════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const FinancialsView = ({
  brokerageData = {}, pmData = {},
  recentTransactions = [], reconciliation = [],
  userRole, user, demoMode, showToast,
}) => {
  // Agents see their personal earnings (CommissionsView)
  if (userRole === 'agent') {
    return <CommissionsView user={user} demoMode={demoMode} showToast={showToast} />;
  }

  // ── Accounting integration data ──
  const [acctData, setAcctData] = useState(null);
  const [acctLoading, setAcctLoading] = useState(false);

  const fetchAcctData = useCallback(async () => {
    if (demoMode) return;
    setAcctLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setAcctLoading(false); return; }
      const resp = await fetch('/api/accounting-sync', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await resp.json();
      if (data.connected) setAcctData(data);
    } catch (err) { console.error('Failed to fetch accounting data:', err); }
    setAcctLoading(false);
  }, [demoMode]);

  useEffect(() => { fetchAcctData(); }, [fetchAcctData]);

  // Navigation: overview | brokerage | agent:{id} | commission_statement | pm | property:{id} | owner_statement:{id}
  const [view, setView] = useState('overview');
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  // Date range for commission statement
  const [stmtFrom, setStmtFrom] = useState('2026-01-01');
  const [stmtTo, setStmtTo] = useState('2026-04-12');
  // PM portfolio view controls
  const [pmViewMode, setPmViewMode] = useState('table'); // 'table' | 'cards'
  const [pmSearch, setPmSearch] = useState('');
  const [pmOwnerFilter, setPmOwnerFilter] = useState('All');
  const [pmOccupancyFilter, setPmOccupancyFilter] = useState('All'); // 'All' | 'Full' | 'Vacancies' | 'Renewals'
  const [pmSortField, setPmSortField] = useState('address');
  const [pmSortDir, setPmSortDir] = useState('asc');
  const [pmPage, setPmPage] = useState(1);
  const PM_PER_PAGE = 25;

  const printRef = useRef(null);

  // ── Navigate helpers ──
  const goOverview = () => { setView('overview'); setSelectedAgentId(null); setSelectedPropertyId(null); };
  const goBrokerage = () => { setView('brokerage'); setSelectedAgentId(null); };
  const goAgent = (id) => { setSelectedAgentId(id); setView('agent'); };
  const goPM = () => { setView('pm'); setSelectedPropertyId(null); };
  const goProperty = (id) => { setSelectedPropertyId(id); setView('property'); };
  const goCommStatement = () => setView('commission_statement');
  const goOwnerStatement = (id) => { setSelectedPropertyId(id); setView('owner_statement'); };

  // ── Data lookups ──
  const selectedAgent = mockAgentCommissions.find(a => a.agentId === selectedAgentId);
  const selectedProperty = mockPMProperties.find(p => p.propertyId === selectedPropertyId);

  // ═══ OVERVIEW (top-level) ═══
  if (view === 'overview') {
    return (
      <div style={{ padding: 24, backgroundColor: theme.colors.bg, minHeight: '100vh' }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: theme.colors.text, margin: 0 }}>Financial Overview</h1>
          <p style={{ fontSize: 14, color: theme.colors.textLight, margin: '6px 0 0' }}>
            Brokerage and Property Management entities — click a card to drill down
          </p>
        </div>

        {/* Entity Cards */}
        <div data-tour="fin-brokerage" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 28 }}>
          {/* Brokerage Card */}
          <div
            onClick={goBrokerage}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = theme.shadow.md; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = theme.shadow.sm; e.currentTarget.style.transform = 'none'; }}
            style={{ ...clickableCard, borderTop: `4px solid ${theme.colors.primary}` }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: 0 }}>Brokerage Company</h2>
              <span style={{ fontSize: 18, color: theme.colors.textLight }}>→</span>
            </div>
            <StatRow label="Revenue MTD" value={fmt(brokerageData.revenueMTD || 18400)} />
            <StatRow label="Pending Commissions" value={fmt(brokerageData.pendingCommissions || 42500)} />
            <StatRow label="Active Deals" value={brokerageData.activeDeals || 4} />
            <StatRow label="Agents" value={mockAgentCommissions.length} />
          </div>

          {/* PM Card */}
          <div
            onClick={goPM}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = theme.shadow.md; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = theme.shadow.sm; e.currentTarget.style.transform = 'none'; }}
            style={{ ...clickableCard, borderTop: `4px solid ${theme.colors.accent}` }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: 0 }}>Property Management</h2>
              <span style={{ fontSize: 18, color: theme.colors.textLight }}>→</span>
            </div>
            <StatRow label="Revenue MTD" value={fmt(pmData.revenueMTD || 8200)} />
            <StatRow label="Pending Amount" value={fmt(pmData.pendingAmount || 3400)} />
            <StatRow label="Active Units" value={pmData.activeUnits || 12} />
            <StatRow label="Properties" value={mockPMProperties.length} />
          </div>
        </div>

        {/* ══ Live Accounting Data (QBO / Xero) ══ */}
        {acctData && acctData.cached && (
          <div style={{ marginBottom: 28 }}>
            {/* Connection Banner */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 16, padding: '10px 16px', borderRadius: theme.radius.md,
              background: `${theme.colors.success}08`, border: `1px solid ${theme.colors.success}20`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: theme.colors.success }}>●</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.text }}>
                  Live from {acctData.connection?.platform === 'quickbooks' ? 'QuickBooks' : 'Xero'}: {acctData.connection?.platform_company_name}
                </span>
                {acctData.connection?.last_sync_at && (
                  <span style={{ fontSize: 11, color: theme.colors.textLight }}>
                    · Last sync {fmtDate(acctData.connection.last_sync_at)}
                  </span>
                )}
              </div>
              <button
                onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) return;
                    await fetch('/api/accounting-sync', {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ action: 'pull_data' }),
                    });
                    fetchAcctData();
                  } catch { /* */ }
                }}
                style={{
                  padding: '5px 14px', borderRadius: 8, border: `1px solid ${theme.colors.success}40`,
                  background: 'white', fontSize: 12, fontWeight: 500, cursor: 'pointer', color: theme.colors.success,
                }}
              >
                ↻ Refresh
              </button>
            </div>

            {/* Account Balances */}
            {acctData.cached.balances?.data?.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: 20, borderLeft: `4px solid ${theme.colors.info}` }}>
                <h2 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: '0 0 16px' }}>Account Balances</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                  {acctData.cached.balances.data.map((acct, i) => (
                    <div key={i} style={{
                      padding: 14, background: theme.colors.bg, borderRadius: theme.radius.md,
                      borderLeft: `3px solid ${acct.balance >= 0 ? theme.colors.success : theme.colors.danger}`,
                    }}>
                      <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 4 }}>{acct.name}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: acct.balance >= 0 ? theme.colors.text : theme.colors.danger }}>
                        {fmt(acct.balance)}
                      </div>
                      <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>{acct.type}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Outstanding Invoices (A/R) */}
            {acctData.cached.outstanding_invoices?.data?.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: 20, borderLeft: `4px solid ${theme.colors.warning}` }}>
                <h2 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: '0 0 16px' }}>
                  Outstanding Receivables ({acctData.cached.outstanding_invoices.data.length})
                </h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Invoice</th>
                        <th style={thStyle}>Customer</th>
                        <th style={thStyle}>Due Date</th>
                        <th style={thStyle}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acctData.cached.outstanding_invoices.data.slice(0, 10).map((inv, i) => (
                        <tr key={i}>
                          <td style={{ ...tdStyle, fontWeight: 500 }}>#{inv.number || inv.id}</td>
                          <td style={tdStyle}>{inv.customer}</td>
                          <td style={{ ...tdStyle, color: new Date(inv.dueDate) < new Date() ? theme.colors.danger : theme.colors.textLight }}>
                            {fmtDate(inv.dueDate)}
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 600, color: theme.colors.warning }}>{fmt(inv.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Accounting Transactions */}
            {acctData.cached.recent_transactions?.data?.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: 20 }}>
                <h2 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: '0 0 16px' }}>
                  Recent Accounting Transactions
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {acctData.cached.recent_transactions.data.slice(0, 15).map((tx, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: 10, backgroundColor: theme.colors.bg, borderRadius: theme.radius.md,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: theme.colors.text }}>{tx.description}</div>
                        <div style={{ display: 'flex', gap: 8, fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>
                          <span>{fmtDate(tx.date)}</span>
                          {tx.account && <span>· {tx.account}</span>}
                        </div>
                      </div>
                      <div style={{
                        fontSize: 14, fontWeight: 600, marginLeft: 12,
                        color: tx.amount >= 0 ? theme.colors.success : theme.colors.danger,
                      }}>{fmt(Math.abs(tx.amount))}{tx.amount < 0 ? ' DR' : ' CR'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reconciliation */}
        <div data-tour="fin-reconciliation" style={{ ...cardStyle, marginBottom: 28 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: '0 0 16px' }}>Reconciliation Status</h2>
          {reconciliation?.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Account</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Last Reconciled</th>
                  </tr>
                </thead>
                <tbody>
                  {reconciliation.map((item, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{item.accountName || item.account}</td>
                      <td style={tdStyle}>
                        <Badge label={item.status} variant={item.status === 'Reconciled' ? 'success' : 'warning'} />
                      </td>
                      <td style={{ ...tdStyle, color: theme.colors.textLight }}>{fmtDate(item.lastReconciledDate) || item.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState text="No reconciliation data available" />}
        </div>

        {/* Recent Transactions */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: '0 0 16px' }}>Recent Transactions</h2>
          {recentTransactions?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recentTransactions.map((tx, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.md,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text, marginBottom: 4 }}>{tx.description}</div>
                    <div style={{ display: 'flex', gap: 10, fontSize: 12, color: theme.colors.textLight }}>
                      <span>{tx.date}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4,
                        background: tx.entity === 'Brokerage' ? `${theme.colors.primary}20` : `${theme.colors.accent}20`,
                        color: tx.entity === 'Brokerage' ? theme.colors.primary : theme.colors.accent,
                      }}>{tx.entity}</span>
                    </div>
                  </div>
                  <div style={{
                    fontSize: 14, fontWeight: 600, marginLeft: 12,
                    color: tx.type === 'income' ? theme.colors.success : theme.colors.danger,
                  }}>{tx.amount}</div>
                </div>
              ))}
            </div>
          ) : <EmptyState text="No recent transactions" />}
        </div>
      </div>
    );
  }

  // ═══ BROKERAGE — Agent Breakdown ═══
  if (view === 'brokerage') {
    const totalGross = mockAgentCommissions.reduce((s, a) => s + a.deals.reduce((ds, d) => ds + d.grossCommission, 0), 0);
    const totalAgentSplits = mockAgentCommissions.reduce((s, a) => s + a.deals.reduce((ds, d) => ds + d.agentSplit, 0), 0);
    const totalBrokerSplits = mockAgentCommissions.reduce((s, a) => s + a.deals.reduce((ds, d) => ds + d.brokerSplit, 0), 0);

    return (
      <div style={{ padding: 24, backgroundColor: theme.colors.bg, minHeight: '100vh' }}>
        <Breadcrumb items={[{ label: 'Financials', onClick: goOverview }, { label: 'Brokerage' }]} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text, margin: 0 }}>Brokerage — Agent Breakdown</h1>
            <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '4px 0 0' }}>Click an agent to see deal-level detail</p>
          </div>
          <button onClick={goCommStatement} style={{ ...printBtnStyle, background: theme.colors.primary, color: 'white', border: 'none' }}>
            🖨️ Commission Statement
          </button>
        </div>

        {/* Summary row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
          <MiniStat label="Gross Commissions" value={fmt(totalGross)} color={theme.colors.primary} />
          <MiniStat label="Agent Splits" value={fmt(totalAgentSplits)} color={theme.colors.accent} />
          <MiniStat label="Broker Revenue" value={fmt(totalBrokerSplits)} color={theme.colors.success} />
          <MiniStat label="Active Agents" value={mockAgentCommissions.length} color={theme.colors.info} />
        </div>

        {/* Agent Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {mockAgentCommissions.map(agent => {
            const totalDeals = agent.deals.length;
            const closedDeals = agent.deals.filter(d => d.status === 'Closed').length;
            const gross = agent.deals.reduce((s, d) => s + d.grossCommission, 0);
            const paid = agent.deals.filter(d => d.paidDate).reduce((s, d) => s + d.agentSplit, 0);
            const pending = agent.deals.filter(d => !d.paidDate).reduce((s, d) => s + d.agentSplit, 0);

            return (
              <div
                key={agent.agentId}
                onClick={() => goAgent(agent.agentId)}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = theme.shadow.md; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = theme.shadow.sm; e.currentTarget.style.transform = 'none'; }}
                style={{ ...clickableCard, borderLeft: `4px solid ${theme.colors.primary}` }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text }}>{agent.agentName}</div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight }}>{agent.role} · {agent.splitPercent}% split</div>
                  </div>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryLight})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 700, fontSize: 14,
                  }}>{agent.agentName.split(' ').map(n => n[0]).join('')}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                  <StatRow label="Gross" value={fmt(gross)} small />
                  <StatRow label="Deals" value={`${closedDeals} closed / ${totalDeals} total`} small />
                  <StatRow label="Paid" value={fmt(paid)} valueColor={theme.colors.success} small />
                  <StatRow label="Pending" value={fmt(pending)} valueColor={theme.colors.warning} small />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ═══ AGENT DETAIL — Deal-by-deal ═══
  if (view === 'agent' && selectedAgent) {
    const gross = selectedAgent.deals.reduce((s, d) => s + d.grossCommission, 0);
    const agentTotal = selectedAgent.deals.reduce((s, d) => s + d.agentSplit, 0);
    const paid = selectedAgent.deals.filter(d => d.paidDate).reduce((s, d) => s + d.agentSplit, 0);
    const pending = agentTotal - paid;

    return (
      <div style={{ padding: 24, backgroundColor: theme.colors.bg, minHeight: '100vh' }}>
        <Breadcrumb items={[
          { label: 'Financials', onClick: goOverview },
          { label: 'Brokerage', onClick: goBrokerage },
          { label: selectedAgent.agentName },
        ]} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text, margin: 0 }}>{selectedAgent.agentName}</h1>
            <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '4px 0 0' }}>{selectedAgent.role} · {selectedAgent.splitPercent}% commission split</p>
          </div>
          <button onClick={() => { setSelectedAgentId(selectedAgent.agentId); setView('agent_print'); }} style={printBtnStyle}>
            🖨️ Print Earnings
          </button>
        </div>

        {/* Agent summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
          <MiniStat label="Gross Commissions" value={fmt(gross)} color={theme.colors.primary} />
          <MiniStat label="Agent Earned" value={fmt(agentTotal)} color={theme.colors.accent} />
          <MiniStat label="Paid" value={fmt(paid)} color={theme.colors.success} />
          <MiniStat label="Pending" value={fmt(pending)} color={theme.colors.warning} />
        </div>

        {/* Deals table */}
        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: '0 0 16px' }}>Deal History</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead>
              <tr>
                <th style={thStyle}>Property</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Sale Price</th>
                <th style={thStyle}>Rate</th>
                <th style={thStyle}>Gross</th>
                <th style={thStyle}>Agent Split</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Close Date</th>
              </tr>
            </thead>
            <tbody>
              {selectedAgent.deals.map(d => (
                <tr key={d.id}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{d.property}</td>
                  <td style={tdStyle}>{d.client}</td>
                  <td style={tdStyle}><Badge label={d.type} variant="default" /></td>
                  <td style={tdStyle}>{fmt(d.price)}</td>
                  <td style={tdStyle}>{d.commissionRate}%</td>
                  <td style={tdStyle}>{fmt(d.grossCommission)}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(d.agentSplit)}</td>
                  <td style={tdStyle}>
                    <Badge label={d.status} variant={d.status === 'Closed' ? 'success' : d.status === 'Pending' ? 'warning' : 'default'} />
                  </td>
                  <td style={{ ...tdStyle, color: theme.colors.textLight }}>{fmtDate(d.closeDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ═══ AGENT PRINT — Earnings Statement ═══
  if (view === 'agent_print' && selectedAgent) {
    const agentTotal = selectedAgent.deals.reduce((s, d) => s + d.agentSplit, 0);
    const paid = selectedAgent.deals.filter(d => d.paidDate).reduce((s, d) => s + d.agentSplit, 0);
    const pending = agentTotal - paid;

    return (
      <div style={{ padding: 24, backgroundColor: theme.colors.bg, minHeight: '100vh' }}>
        <Breadcrumb items={[
          { label: 'Financials', onClick: goOverview },
          { label: 'Brokerage', onClick: goBrokerage },
          { label: selectedAgent.agentName, onClick: () => goAgent(selectedAgent.agentId) },
          { label: 'Earnings Statement' },
        ]} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button onClick={() => handlePrint(printRef)} style={{ ...printBtnStyle, background: theme.colors.primary, color: 'white', border: 'none' }}>
            🖨️ Print / Save PDF
          </button>
        </div>

        <div ref={printRef} style={{ ...cardStyle, maxWidth: 800, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, paddingBottom: 16, borderBottom: `3px solid ${theme.colors.primary}` }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 800, color: theme.colors.primary }}>SABAL</div>
              <div style={{ fontSize: 11, color: theme.colors.textLight }}>Sabal Real Estate NJ · Ridgewood, NJ</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>Agent Earnings Statement</div>
              <div style={{ fontSize: 12, color: theme.colors.textLight }}>Generated {fmtDate(new Date().toISOString())}</div>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text }}>{selectedAgent.agentName}</div>
            <div style={{ fontSize: 13, color: theme.colors.textLight }}>{selectedAgent.role} · {selectedAgent.splitPercent}% Commission Split</div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <thead>
              <tr>
                <th style={thStyle}>Property</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Sale Price</th>
                <th style={thStyle}>Gross</th>
                <th style={thStyle}>Your Split</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Paid Date</th>
              </tr>
            </thead>
            <tbody>
              {selectedAgent.deals.map(d => (
                <tr key={d.id}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{d.property}</td>
                  <td style={tdStyle}>{d.type}</td>
                  <td style={tdStyle}>{fmt(d.price)}</td>
                  <td style={tdStyle}>{fmt(d.grossCommission)}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(d.agentSplit)}</td>
                  <td style={tdStyle}>{d.status}</td>
                  <td style={{ ...tdStyle, color: theme.colors.textLight }}>{fmtDate(d.paidDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ padding: 16, background: theme.colors.bg, borderRadius: 8 }}>
            <TotalRow label="Total Earned" value={fmt(agentTotal)} />
            <TotalRow label="Paid to Date" value={fmt(paid)} />
            <TotalRow label="Pending" value={fmt(pending)} bold />
          </div>
        </div>
      </div>
    );
  }

  // ═══ COMMISSION STATEMENT — Broker view, printable ═══
  if (view === 'commission_statement') {
    // Filter deals by date range
    const allDeals = mockAgentCommissions.flatMap(a =>
      a.deals.map(d => ({ ...d, agentName: a.agentName, splitPercent: a.splitPercent }))
    );
    const filtered = allDeals.filter(d => {
      const cd = d.closeDate || d.paidDate;
      if (!cd) return true; // Include active/pending deals
      return cd >= stmtFrom && cd <= stmtTo;
    });
    const totalGross = filtered.reduce((s, d) => s + d.grossCommission, 0);
    const totalAgentSplits = filtered.reduce((s, d) => s + d.agentSplit, 0);
    const totalBrokerSplits = filtered.reduce((s, d) => s + d.brokerSplit, 0);

    return (
      <div style={{ padding: 24, backgroundColor: theme.colors.bg, minHeight: '100vh' }}>
        <Breadcrumb items={[
          { label: 'Financials', onClick: goOverview },
          { label: 'Brokerage', onClick: goBrokerage },
          { label: 'Commission Statement' },
        ]} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text, margin: 0 }}>Commission Statement</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 13, color: theme.colors.textLight }}>From</label>
            <input type="date" value={stmtFrom} onChange={e => setStmtFrom(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 13 }} />
            <label style={{ fontSize: 13, color: theme.colors.textLight }}>To</label>
            <input type="date" value={stmtTo} onChange={e => setStmtTo(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 13 }} />
            <button onClick={() => handlePrint(printRef)} style={{ ...printBtnStyle, background: theme.colors.primary, color: 'white', border: 'none' }}>
              🖨️ Print
            </button>
          </div>
        </div>

        <div ref={printRef} style={{ ...cardStyle, maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, paddingBottom: 16, borderBottom: `3px solid ${theme.colors.primary}` }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 800, color: theme.colors.primary }}>SABAL</div>
              <div style={{ fontSize: 11, color: theme.colors.textLight }}>Sabal Real Estate NJ · Ridgewood, NJ</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>Commission Statement</div>
              <div style={{ fontSize: 12, color: theme.colors.textLight }}>{fmtDate(stmtFrom)} — {fmtDate(stmtTo)}</div>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <thead>
              <tr>
                <th style={thStyle}>Agent</th>
                <th style={thStyle}>Property</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Sale Price</th>
                <th style={thStyle}>Gross</th>
                <th style={thStyle}>Agent Split</th>
                <th style={thStyle}>Broker Split</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => (
                <tr key={i}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{d.agentName}</td>
                  <td style={tdStyle}>{d.property}</td>
                  <td style={tdStyle}>{d.type}</td>
                  <td style={tdStyle}>{fmt(d.price)}</td>
                  <td style={tdStyle}>{fmt(d.grossCommission)}</td>
                  <td style={tdStyle}>{fmt(d.agentSplit)}</td>
                  <td style={tdStyle}>{fmt(d.brokerSplit)}</td>
                  <td style={tdStyle}>
                    <Badge label={d.status} variant={d.status === 'Closed' ? 'success' : d.status === 'Pending' ? 'warning' : 'default'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ padding: 16, background: theme.colors.bg, borderRadius: 8 }}>
            <TotalRow label="Gross Commissions" value={fmt(totalGross)} />
            <TotalRow label="Total Agent Splits" value={fmt(totalAgentSplits)} />
            <TotalRow label="Broker Revenue" value={fmt(totalBrokerSplits)} bold />
          </div>
        </div>
      </div>
    );
  }

  // ═══ PM — Property Breakdown (Table + Cards toggle) ═══
  if (view === 'pm') {
    // Unique owners for filter dropdown
    const uniqueOwners = [...new Set(mockPMProperties.map(p => p.owner))].sort();

    // Filter + search
    const filteredProperties = mockPMProperties.filter(prop => {
      const matchesSearch = !pmSearch ||
        prop.address.toLowerCase().includes(pmSearch.toLowerCase()) ||
        prop.owner.toLowerCase().includes(pmSearch.toLowerCase());
      const matchesOwner = pmOwnerFilter === 'All' || prop.owner === pmOwnerFilter;
      const hasVacancy = prop.occupiedUnits < prop.units;
      const hasRenewals = prop.leases.some(l => l.status === 'Renewal Pending');
      const matchesOccupancy =
        pmOccupancyFilter === 'All' ? true :
        pmOccupancyFilter === 'Full' ? !hasVacancy :
        pmOccupancyFilter === 'Vacancies' ? hasVacancy :
        pmOccupancyFilter === 'Renewals' ? hasRenewals : true;
      return matchesSearch && matchesOwner && matchesOccupancy;
    });

    // Sort
    const sortedProperties = [...filteredProperties].sort((a, b) => {
      let va, vb;
      switch (pmSortField) {
        case 'address': va = a.address; vb = b.address; break;
        case 'owner': va = a.owner; vb = b.owner; break;
        case 'units': va = a.units; vb = b.units; break;
        case 'rent': va = a.monthlyRent; vb = b.monthlyRent; break;
        case 'occupancy': va = a.occupiedUnits / a.units; vb = b.occupiedUnits / b.units; break;
        case 'fee': va = a.mgmtFeeMonthly; vb = b.mgmtFeeMonthly; break;
        case 'ytdRevenue': va = a.ytdRevenue; vb = b.ytdRevenue; break;
        default: va = a.address; vb = b.address;
      }
      if (typeof va === 'string') return pmSortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return pmSortDir === 'asc' ? va - vb : vb - va;
    });

    // Pagination
    const totalPages = Math.ceil(sortedProperties.length / PM_PER_PAGE);
    const pagedProperties = sortedProperties.slice((pmPage - 1) * PM_PER_PAGE, pmPage * PM_PER_PAGE);

    // Totals (from ALL properties, not filtered)
    const totalRent = mockPMProperties.reduce((s, p) => s + p.monthlyRent, 0);
    const totalFees = mockPMProperties.reduce((s, p) => s + p.mgmtFeeMonthly, 0);
    const totalUnits = mockPMProperties.reduce((s, p) => s + p.units, 0);
    const occupiedUnits = mockPMProperties.reduce((s, p) => s + p.occupiedUnits, 0);
    const vacantCount = mockPMProperties.reduce((s, p) => s + (p.units - p.occupiedUnits), 0);
    const renewalCount = mockPMProperties.reduce((s, p) => s + p.leases.filter(l => l.status === 'Renewal Pending').length, 0);

    const handleSort = (field) => {
      if (pmSortField === field) {
        setPmSortDir(pmSortDir === 'asc' ? 'desc' : 'asc');
      } else {
        setPmSortField(field);
        setPmSortDir('asc');
      }
    };

    const SortIcon = ({ field }) => {
      if (pmSortField !== field) return <span style={{ color: theme.colors.border, fontSize: 10, marginLeft: 3 }}>⇅</span>;
      return <span style={{ color: theme.colors.primary, fontSize: 10, marginLeft: 3 }}>{pmSortDir === 'asc' ? '▲' : '▼'}</span>;
    };

    const sortableTh = (label, field, extraStyle = {}) => (
      <th onClick={() => handleSort(field)} style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...extraStyle }}>
        {label}<SortIcon field={field} />
      </th>
    );

    return (
      <div style={{ padding: 24, backgroundColor: theme.colors.bg, minHeight: '100vh' }}>
        <Breadcrumb items={[{ label: 'Financials', onClick: goOverview }, { label: 'Property Management' }]} />

        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text, margin: 0 }}>Property Management — Portfolio</h1>
          <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '4px 0 0' }}>
            {mockPMProperties.length} properties · Click a row for full breakdown and owner statement
          </p>
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
          <MiniStat label="Monthly Rent Roll" value={fmt(totalRent)} color={theme.colors.primary} />
          <MiniStat label="Mgmt Fees/mo" value={fmt(totalFees)} color={theme.colors.accent} />
          <MiniStat label="Total Units" value={totalUnits} color={theme.colors.info} />
          <MiniStat label="Occupancy" value={pct(occupiedUnits, totalUnits)} color={theme.colors.success} />
          <MiniStat label="Vacant Units" value={vacantCount} color={vacantCount > 0 ? theme.colors.danger : theme.colors.success} />
          <MiniStat label="Pending Renewals" value={renewalCount} color={renewalCount > 0 ? theme.colors.warning : theme.colors.success} />
        </div>

        {/* Search, Filters, View Toggle */}
        <div style={{ ...cardStyle, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{
            flex: '1 1 200px', display: 'flex', alignItems: 'center', gap: 6,
            background: theme.colors.bg, border: `1px solid ${theme.colors.border}`,
            borderRadius: 8, padding: '0 10px',
          }}>
            <span style={{ color: theme.colors.textLight, fontSize: 14 }}>🔍</span>
            <input
              value={pmSearch}
              onChange={e => { setPmSearch(e.target.value); setPmPage(1); }}
              placeholder="Search address or owner..."
              style={{ border: 'none', outline: 'none', width: '100%', padding: '8px 0', fontSize: 13, background: 'transparent' }}
            />
          </div>

          {/* Owner filter */}
          <select
            value={pmOwnerFilter}
            onChange={e => { setPmOwnerFilter(e.target.value); setPmPage(1); }}
            style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, fontSize: 13, background: 'white', cursor: 'pointer' }}
          >
            <option value="All">All Owners</option>
            {uniqueOwners.map(o => <option key={o} value={o}>{o}</option>)}
          </select>

          {/* Occupancy filter */}
          <select
            value={pmOccupancyFilter}
            onChange={e => { setPmOccupancyFilter(e.target.value); setPmPage(1); }}
            style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, fontSize: 13, background: 'white', cursor: 'pointer' }}
          >
            <option value="All">All Statuses</option>
            <option value="Full">Fully Occupied</option>
            <option value="Vacancies">Has Vacancies</option>
            <option value="Renewals">Pending Renewals</option>
          </select>

          {/* View toggle */}
          <div style={{ display: 'flex', borderRadius: 8, border: `1px solid ${theme.colors.border}`, overflow: 'hidden' }}>
            <button
              onClick={() => setPmViewMode('table')}
              style={{
                padding: '7px 12px', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: pmViewMode === 'table' ? theme.colors.primary : 'white',
                color: pmViewMode === 'table' ? 'white' : theme.colors.textLight,
              }}
            >☰ Table</button>
            <button
              onClick={() => setPmViewMode('cards')}
              style={{
                padding: '7px 12px', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                borderLeft: `1px solid ${theme.colors.border}`,
                background: pmViewMode === 'cards' ? theme.colors.primary : 'white',
                color: pmViewMode === 'cards' ? 'white' : theme.colors.textLight,
              }}
            >▦ Cards</button>
          </div>

          {/* Result count */}
          <span style={{ fontSize: 12, color: theme.colors.textLight }}>
            {filteredProperties.length === mockPMProperties.length ? `${mockPMProperties.length} properties` : `${filteredProperties.length} of ${mockPMProperties.length}`}
          </span>
        </div>

        {/* ── TABLE VIEW ── */}
        {pmViewMode === 'table' && (
          <div style={{ ...cardStyle, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead>
                <tr>
                  {sortableTh('Address', 'address', { minWidth: 160 })}
                  {sortableTh('Owner', 'owner', { minWidth: 140 })}
                  {sortableTh('Units', 'units')}
                  {sortableTh('Monthly Rent', 'rent')}
                  {sortableTh('Occupancy', 'occupancy')}
                  {sortableTh('Mgmt Fee', 'fee')}
                  {sortableTh('YTD Revenue', 'ytdRevenue')}
                  <th style={thStyle}>Alerts</th>
                </tr>
              </thead>
              <tbody>
                {pagedProperties.map(prop => {
                  const occ = pct(prop.occupiedUnits, prop.units);
                  const vacancies = prop.units - prop.occupiedUnits;
                  const renewals = prop.leases.filter(l => l.status === 'Renewal Pending').length;

                  return (
                    <tr
                      key={prop.propertyId}
                      onClick={() => goProperty(prop.propertyId)}
                      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${theme.colors.primary}06`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600, color: theme.colors.primary }}>{prop.address}</td>
                      <td style={tdStyle}>{prop.owner}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{prop.occupiedUnits}/{prop.units}</td>
                      <td style={tdStyle}>{fmt(prop.monthlyRent)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: theme.colors.border, minWidth: 40 }}>
                            <div style={{ height: '100%', borderRadius: 3, width: occ, background: vacancies > 0 ? theme.colors.warning : theme.colors.success }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 500, color: vacancies > 0 ? theme.colors.warning : theme.colors.success }}>{occ}</span>
                        </div>
                      </td>
                      <td style={tdStyle}>{fmt(prop.mgmtFeeMonthly)}<span style={{ fontSize: 10, color: theme.colors.textLight }}> ({prop.mgmtFeePercent}%)</span></td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: theme.colors.success }}>{fmt(prop.ytdRevenue)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {vacancies > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 6, background: `${theme.colors.danger}12`, color: theme.colors.danger }}>
                              {vacancies} vacant
                            </span>
                          )}
                          {renewals > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 6, background: `${theme.colors.warning}12`, color: theme.colors.warning }}>
                              {renewals} renewal{renewals > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: `1px solid ${theme.colors.border}`, marginTop: 8 }}>
                <span style={{ fontSize: 12, color: theme.colors.textLight }}>
                  Showing {(pmPage - 1) * PM_PER_PAGE + 1}–{Math.min(pmPage * PM_PER_PAGE, sortedProperties.length)} of {sortedProperties.length}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button disabled={pmPage === 1} onClick={() => setPmPage(pmPage - 1)}
                    style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, background: 'white', cursor: pmPage === 1 ? 'default' : 'pointer', opacity: pmPage === 1 ? 0.4 : 1, fontSize: 12 }}>
                    ← Prev
                  </button>
                  <span style={{ padding: '5px 10px', fontSize: 12, color: theme.colors.text, fontWeight: 600 }}>
                    {pmPage} / {totalPages}
                  </span>
                  <button disabled={pmPage === totalPages} onClick={() => setPmPage(pmPage + 1)}
                    style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, background: 'white', cursor: pmPage === totalPages ? 'default' : 'pointer', opacity: pmPage === totalPages ? 0.4 : 1, fontSize: 12 }}>
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── CARD VIEW ── */}
        {pmViewMode === 'cards' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {pagedProperties.map(prop => {
              const occupancy = pct(prop.occupiedUnits, prop.units);
              const renewals = prop.leases.filter(l => l.status === 'Renewal Pending').length;

              return (
                <div
                  key={prop.propertyId}
                  onClick={() => goProperty(prop.propertyId)}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = theme.shadow.md; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = theme.shadow.sm; e.currentTarget.style.transform = 'none'; }}
                  style={{ ...clickableCard, borderLeft: `4px solid ${theme.colors.accent}` }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text }}>{prop.address}</div>
                      <div style={{ fontSize: 12, color: theme.colors.textLight }}>Owner: {prop.owner}</div>
                    </div>
                    {renewals > 0 && (
                      <span style={{
                        padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: `${theme.colors.warning}20`, color: theme.colors.warning,
                      }}>{renewals} renewal{renewals > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                    <StatRow label="Monthly Rent" value={fmt(prop.monthlyRent)} small />
                    <StatRow label="Occupancy" value={occupancy} small />
                    <StatRow label="Mgmt Fee" value={`${fmt(prop.mgmtFeeMonthly)}/mo (${prop.mgmtFeePercent}%)`} small />
                    <StatRow label="YTD Revenue" value={fmt(prop.ytdRevenue)} valueColor={theme.colors.success} small />
                  </div>
                </div>
              );
            })}

            {/* Card view pagination */}
            {totalPages > 1 && (
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', gap: 8, padding: '12px 0' }}>
                <button disabled={pmPage === 1} onClick={() => setPmPage(pmPage - 1)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: 'white', cursor: pmPage === 1 ? 'default' : 'pointer', opacity: pmPage === 1 ? 0.4 : 1, fontSize: 13 }}>
                  ← Prev
                </button>
                <span style={{ padding: '6px 10px', fontSize: 13, color: theme.colors.text, fontWeight: 600 }}>
                  Page {pmPage} of {totalPages}
                </span>
                <button disabled={pmPage === totalPages} onClick={() => setPmPage(pmPage + 1)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: 'white', cursor: pmPage === totalPages ? 'default' : 'pointer', opacity: pmPage === totalPages ? 0.4 : 1, fontSize: 13 }}>
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {filteredProperties.length === 0 && (
          <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 14, color: theme.colors.textLight }}>No properties match your filters</div>
            <button onClick={() => { setPmSearch(''); setPmOwnerFilter('All'); setPmOccupancyFilter('All'); }}
              style={{ marginTop: 12, padding: '6px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: 'white', fontSize: 13, cursor: 'pointer', color: theme.colors.primary }}>
              Clear Filters
            </button>
          </div>
        )}
      </div>
    );
  }

  // ═══ PROPERTY DETAIL ═══
  if (view === 'property' && selectedProperty) {
    const p = selectedProperty;
    const noi = p.ytdRevenue - p.ytdExpenses;

    return (
      <div style={{ padding: 24, backgroundColor: theme.colors.bg, minHeight: '100vh' }}>
        <Breadcrumb items={[
          { label: 'Financials', onClick: goOverview },
          { label: 'Property Management', onClick: goPM },
          { label: p.address },
        ]} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text, margin: 0 }}>{p.address}</h1>
            <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '4px 0 0' }}>Owner: {p.owner} · {p.units} unit{p.units > 1 ? 's' : ''} · {p.mgmtFeePercent}% management fee</p>
          </div>
          <button onClick={() => goOwnerStatement(p.propertyId)} style={{ ...printBtnStyle, background: theme.colors.accent, color: 'white', border: 'none' }}>
            📄 Owner Statement
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
          <MiniStat label="Monthly Rent" value={fmt(p.monthlyRent)} color={theme.colors.primary} />
          <MiniStat label="YTD Revenue" value={fmt(p.ytdRevenue)} color={theme.colors.success} />
          <MiniStat label="YTD Expenses" value={fmt(p.ytdExpenses)} color={theme.colors.danger} />
          <MiniStat label="YTD NOI" value={fmt(noi)} color={noi >= 0 ? theme.colors.success : theme.colors.danger} />
        </div>

        {/* Leases */}
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: '0 0 16px' }}>Lease Schedule</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Unit</th>
                  <th style={thStyle}>Tenant</th>
                  <th style={thStyle}>Rent</th>
                  <th style={thStyle}>Lease End</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {p.leases.map((l, i) => (
                  <tr key={i}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{l.unit}</td>
                    <td style={tdStyle}>{l.tenant || '—'}</td>
                    <td style={tdStyle}>{fmt(l.rent)}</td>
                    <td style={{ ...tdStyle, color: theme.colors.textLight }}>{fmtDate(l.leaseEnd)}</td>
                    <td style={tdStyle}>
                      <Badge label={l.status} variant={l.status === 'Active' ? 'success' : l.status === 'Vacant' ? 'danger' : 'warning'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Expenses */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: '0 0 16px' }}>Recent Expenses</h3>
          {p.recentExpenses.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Description</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {p.recentExpenses.map((e, i) => (
                    <tr key={i}>
                      <td style={{ ...tdStyle, color: theme.colors.textLight }}>{fmtDate(e.date)}</td>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{e.description}</td>
                      <td style={tdStyle}>{e.category}</td>
                      <td style={{ ...tdStyle, color: theme.colors.danger, fontWeight: 600 }}>-{fmt(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState text="No expenses recorded" />}
        </div>
      </div>
    );
  }

  // ═══ OWNER STATEMENT — Printable ═══
  if (view === 'owner_statement' && selectedProperty) {
    const p = selectedProperty;
    const noi = p.ytdRevenue - p.ytdExpenses;
    const totalExpenses = p.recentExpenses.reduce((s, e) => s + e.amount, 0);
    const totalRentCollected = p.monthlyRent * 4; // approx YTD (4 months)
    const ownerDisbursement = totalRentCollected - totalExpenses - (p.mgmtFeeMonthly * 4);

    return (
      <div style={{ padding: 24, backgroundColor: theme.colors.bg, minHeight: '100vh' }}>
        <Breadcrumb items={[
          { label: 'Financials', onClick: goOverview },
          { label: 'Property Management', onClick: goPM },
          { label: p.address, onClick: () => goProperty(p.propertyId) },
          { label: 'Owner Statement' },
        ]} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button onClick={() => handlePrint(printRef)} style={{ ...printBtnStyle, background: theme.colors.accent, color: 'white', border: 'none' }}>
            🖨️ Print / Save PDF
          </button>
        </div>

        <div ref={printRef} style={{ ...cardStyle, maxWidth: 800, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, paddingBottom: 16, borderBottom: `3px solid ${theme.colors.accent}` }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 800, color: theme.colors.primary }}>SABAL</div>
              <div style={{ fontSize: 11, color: theme.colors.textLight }}>Sabal Real Estate NJ · Ridgewood, NJ</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>Owner Statement</div>
              <div style={{ fontSize: 12, color: theme.colors.textLight }}>Period: Jan 1 — Apr 12, 2026</div>
            </div>
          </div>

          <div style={{ marginBottom: 20, padding: 16, background: theme.colors.bg, borderRadius: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text }}>{p.address}</div>
            <div style={{ fontSize: 13, color: theme.colors.textLight, marginTop: 4 }}>Owner: {p.owner} · {p.units} unit{p.units > 1 ? 's' : ''}</div>
          </div>

          <h4 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, margin: '0 0 12px' }}>Income</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <thead>
              <tr>
                <th style={thStyle}>Unit</th>
                <th style={thStyle}>Tenant</th>
                <th style={thStyle}>Monthly Rent</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {p.leases.map((l, i) => (
                <tr key={i}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{l.unit}</td>
                  <td style={tdStyle}>{l.tenant || '(Vacant)'}</td>
                  <td style={tdStyle}>{fmt(l.rent)}</td>
                  <td style={tdStyle}>{l.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {p.recentExpenses.length > 0 && (
            <>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, margin: '0 0 12px' }}>Expenses</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Description</th>
                    <th style={thStyle}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {p.recentExpenses.map((e, i) => (
                    <tr key={i}>
                      <td style={{ ...tdStyle, color: theme.colors.textLight }}>{fmtDate(e.date)}</td>
                      <td style={tdStyle}>{e.description}</td>
                      <td style={{ ...tdStyle, color: theme.colors.danger }}>{fmt(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div style={{ padding: 16, background: theme.colors.bg, borderRadius: 8 }}>
            <TotalRow label="Total Rent Collected (YTD)" value={fmt(totalRentCollected)} />
            <TotalRow label="Total Expenses" value={`-${fmt(totalExpenses)}`} />
            <TotalRow label={`Management Fee (${p.mgmtFeePercent}%)`} value={`-${fmt(p.mgmtFeeMonthly * 4)}`} />
            <TotalRow label="Owner Disbursement" value={fmt(ownerDisbursement)} bold />
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight }}>Loading...</div>;
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const Breadcrumb = ({ items }) => (
  <div style={breadcrumbStyle}>
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span style={{ color: theme.colors.textLight }}>›</span>}
        {item.onClick ? (
          <span onClick={item.onClick} style={breadLink}>{item.label}</span>
        ) : (
          <span style={{ fontWeight: 600, color: theme.colors.text }}>{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const StatRow = ({ label, value, valueColor, small }) => (
  <div style={{ marginBottom: small ? 0 : 10 }}>
    <div style={{ fontSize: small ? 11 : 12, color: theme.colors.textLight, marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: small ? 13 : 18, fontWeight: 600, color: valueColor || theme.colors.text }}>{value}</div>
  </div>
);

const MiniStat = ({ label, value, color }) => (
  <div style={{
    ...cardStyle, padding: '14px 16px',
    borderLeft: `3px solid ${color}`,
  }}>
    <div style={{ fontSize: 11, color: theme.colors.textLight, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text }}>{value}</div>
  </div>
);

const TotalRow = ({ label, value, bold }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', padding: '5px 0',
    fontSize: bold ? 16 : 14, fontWeight: bold ? 700 : 400,
    borderTop: bold ? `2px solid ${theme.colors.border}` : 'none',
    paddingTop: bold ? 10 : 5, marginTop: bold ? 8 : 0,
  }}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const EmptyState = ({ text }) => (
  <div style={{ color: theme.colors.textLight, fontSize: 14, padding: '20px 0' }}>{text}</div>
);

export default FinancialsView;
